import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Loader2, ArrowLeft, Share2, X, Download, Sparkles, Info, ExternalLink, ChevronDown, ChevronUp, LogOut, Plus, Lock } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import ExportModal from '@/components/ExportModal';
import CreditsDisplay from '@/components/CreditsDisplay';
import ThemePreviewModal from '@/components/ThemePreviewModal';
import WowCXLogo from '@/components/WowCXLogo';
import AddPageModal from '@/components/AddPageModal';
import { 
  PreviewChatPanel, 
  ShareModal, 
  PreviewToolbar, 
  PreviewIframe,
  ChatToggleButton 
} from '@/components/preview';
import { useWorkspace } from '@/context/WorkspaceContext';

// Use current origin for API calls
const API = `${window.location.origin}/api`;

// WordPress staging preview URL
const WP_STAGING_URL = 'https://wowcx.io/staging';

const DesignPreviewPage = () => {
  const { projectId } = useParams();
  const [searchParams] = useSearchParams();
  const requestedDesignId = searchParams.get('design');
  const navigate = useNavigate();
  const { credits, refreshCredits, user } = useWorkspace();
  const [project, setProject] = useState(null);
  const [designs, setDesigns] = useState([]);
  const [currentDesign, setCurrentDesign] = useState(null);
  const [revisionPrompt, setRevisionPrompt] = useState('');
  const [loading, setLoading] = useState(true);
  const [revising, setRevising] = useState(false);
  const [viewMode, setViewMode] = useState('visual'); // 'visual' or 'technical'
  const [selectedTheme, setSelectedTheme] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  const [sharingLoading, setSharingLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showThemePreview, setShowThemePreview] = useState(false);
  const [showHowWeDidIt, setShowHowWeDidIt] = useState(false);
  const [sidePanelTab, setSidePanelTab] = useState('revisions');
  const [showAddPageModal, setShowAddPageModal] = useState(false);
  const [responsiveViewMode, setResponsiveViewMode] = useState('desktop');
  const [revisionMode, setRevisionMode] = useState('quick');
  const [advancedJobId, setAdvancedJobId] = useState(null);
  const [advancedProgress, setAdvancedProgress] = useState('');
  const [previewLoading, setPreviewLoading] = useState(true);
  const [previewToken, setPreviewToken] = useState(null);
  const [previewError, setPreviewError] = useState(null);
  const [previewTimeout, setPreviewTimeout] = useState(false);
  const [activePage, setActivePage] = useState(0);
  const [updatingImages, setUpdatingImages] = useState(false);
  const [imageSource, setImageSource] = useState('theme_default');
  const [chatOpen, setChatOpen] = useState(false);
  
  // Inspect Mode States
  const [inspectMode, setInspectMode] = useState(false);
  const [selectedElement, setSelectedElement] = useState(null);
  const [iframeReady, setIframeReady] = useState(false);
  const iframeRef = useRef(null);

  useEffect(() => {
    fetchProjectAndDesigns();
  }, [projectId, requestedDesignId]);

  // Fetch theme when project is loaded
  useEffect(() => {
    const loadTheme = async () => {
      if (project?.selected_theme_id) {
        try {
          const res = await axios.get(`${API}/themes`, { withCredentials: true });
          const themes = res.data.themes || res.data || [];
          const theme = themes.find(t => t.theme_id === project.selected_theme_id);
          if (theme) {
            setSelectedTheme(theme);
          } else if (project.selected_theme_id.startsWith('wp_org_')) {
            // Create a minimal theme object for WP.org themes
            setSelectedTheme({
              theme_id: project.selected_theme_id,
              name: project.selected_theme_id.replace('wp_org_', '').replace(/-/g, ' '),
              color_scheme: currentDesign?.elementor_json?.theme_colors || {
                primary: '#1a1a2e',
                secondary: '#f5f5f5',
                accent: '#4361ee'
              }
            });
          }
        } catch (err) {
          console.error('Failed to load theme:', err);
        }
      }
    };
    loadTheme();
  }, [project?.selected_theme_id, currentDesign]);

  // Generate preview token when design changes
  useEffect(() => {
    const generatePreviewToken = async () => {
      if (currentDesign?.design_id) {
        setPreviewLoading(true);
        setPreviewError(null);
        setPreviewTimeout(false);
        try {
          const res = await axios.post(
            `${API}/designs/${currentDesign.design_id}/preview-token`,
            {},
            { withCredentials: true }
          );
          setPreviewToken(res.data.token);
        } catch (err) {
          console.error('Failed to generate preview token:', err);
          setPreviewError('Failed to generate preview token. Please try refreshing.');
          setPreviewLoading(false);
        }
      }
    };
    generatePreviewToken();
  }, [currentDesign?.design_id]);

  // Timeout for preview loading (15 seconds)
  useEffect(() => {
    if (previewLoading && previewToken) {
      const timeout = setTimeout(() => {
        if (previewLoading) {
          setPreviewTimeout(true);
          setPreviewLoading(false);
        }
      }, 15000);
      return () => clearTimeout(timeout);
    }
  }, [previewLoading, previewToken]);

  // Handle postMessage from iframe (Inspect Mode)
  useEffect(() => {
    const handleMessage = (event) => {
      // Security: Only accept messages from WordPress staging site
      const wpStagingOrigin = new URL(WP_STAGING_URL).origin;
      if (event.origin !== wpStagingOrigin && event.origin !== window.location.origin) {
        return; // Ignore messages from untrusted origins
      }
      
      const data = event.data;
      if (!data || !data.type) return;

      switch (data.type) {
        case 'DS_IFRAME_READY':
          console.log('[DesignPreview] Iframe ready, version:', data.version);
          setIframeReady(true);
          break;
        case 'DS_PONG':
          console.log('[DesignPreview] Iframe responded to ping');
          setIframeReady(true);
          break;
        case 'DS_ELEMENT_SELECTED':
          console.log('[DesignPreview] Element selected:', data.element);
          setSelectedElement(data.element);
          // Auto-open chat panel when element is selected
          if (!chatOpen) setChatOpen(true);
          break;
        default:
          break;
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [chatOpen]);

  // Ping iframe to check if it's ready (fallback if DS_IFRAME_READY wasn't received)
  useEffect(() => {
    if (iframeReady || !previewToken) return;
    
    // After iframe loads, ping it periodically to check readiness
    const pingInterval = setInterval(() => {
      if (iframeRef.current && iframeRef.current.contentWindow) {
        const wpStagingOrigin = new URL(WP_STAGING_URL).origin;
        iframeRef.current.contentWindow.postMessage({ type: 'DS_PING' }, wpStagingOrigin);
      }
    }, 1000);
    
    // Stop pinging after 10 seconds and enable inspect mode anyway with warning
    const timeout = setTimeout(() => {
      clearInterval(pingInterval);
      if (!iframeReady) {
        console.log('[DesignPreview] Iframe did not respond to ping, enabling inspect mode anyway');
        setIframeReady(true); // Enable button anyway so user can try
      }
    }, 10000);
    
    return () => {
      clearInterval(pingInterval);
      clearTimeout(timeout);
    };
  }, [iframeReady, previewToken]);

  // Send message to iframe
  const sendToIframe = useCallback((message) => {
    if (iframeRef.current && iframeRef.current.contentWindow) {
      const wpStagingOrigin = new URL(WP_STAGING_URL).origin;
      iframeRef.current.contentWindow.postMessage(message, wpStagingOrigin);
    }
  }, []);

  // Toggle inspect mode
  const toggleInspectMode = useCallback(() => {
    const newState = !inspectMode;
    setInspectMode(newState);
    
    if (newState) {
      sendToIframe({ type: 'DS_ENABLE_INSPECT_MODE' });
      toast.info('Click any element in the preview to select it for editing', { duration: 3000 });
    } else {
      sendToIframe({ type: 'DS_DISABLE_INSPECT_MODE' });
      sendToIframe({ type: 'DS_CLEAR_SELECTION' });
      setSelectedElement(null);
    }
  }, [inspectMode, sendToIframe]);

  // Clear element selection
  const clearElementSelection = useCallback(() => {
    setSelectedElement(null);
    sendToIframe({ type: 'DS_CLEAR_SELECTION' });
  }, [sendToIframe]);

  // Handle edit element - pre-fill revision prompt with context
  const handleEditElement = useCallback((element) => {
    // Build context-aware prompt prefix
    const sectionName = element.sectionType?.replace(/_/g, ' ') || 'section';
    const elementType = element.elementType?.replace(/_/g, ' ') || 'element';
    const currentText = element.currentText?.substring(0, 100) || '';
    
    // Pre-fill the revision prompt with context
    const contextPrompt = `In the ${sectionName} section, change the ${elementType}${currentText ? ` (currently: "${currentText}...")` : ''} to: `;
    setRevisionPrompt(contextPrompt);
    
    // Open chat panel and switch to revisions tab
    setChatOpen(true);
    setSidePanelTab('revisions');
    
    // Focus the textarea
    setTimeout(() => {
      const textarea = document.querySelector('[data-testid="revision-input"]');
      if (textarea) {
        textarea.focus();
        // Move cursor to end
        textarea.setSelectionRange(textarea.value.length, textarea.value.length);
      }
    }, 100);
  }, []);

  const fetchProjectAndDesigns = async () => {
    try {
      setLoading(true);
      const projectRes = await axios.get(`${API}/projects/${projectId}`, { withCredentials: true });
      setProject(projectRes.data);
      
      const designsRes = await axios.get(`${API}/projects/${projectId}/designs`, { withCredentials: true });
      const designsList = designsRes.data.designs || [];
      setDesigns(designsList);
      
      if (requestedDesignId) {
        const requested = designsList.find(d => d.design_id === requestedDesignId);
        if (requested) {
          setCurrentDesign(requested);
          setImageSource(requested.images?.source || 'theme_default');
        } else if (designsList.length > 0) {
          setCurrentDesign(designsList[0]);
          setImageSource(designsList[0].images?.source || 'theme_default');
        }
      } else if (designsList.length > 0) {
        setCurrentDesign(designsList[0]);
        setImageSource(designsList[0].images?.source || 'theme_default');
      }
    } catch (error) {
      console.error('Fetch error:', error);
      toast.error('Failed to load project');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleReviseDesign = async () => {
    if (!revisionPrompt.trim()) {
      toast.error('Please describe what changes you want');
      return;
    }
    
    setRevising(true);
    
    try {
      if (revisionMode === 'quick') {
        const response = await axios.post(
          `${API}/ai/revise-design/quick`,
          {
            design_id: currentDesign.design_id,
            revision_prompt: revisionPrompt
          },
          { withCredentials: true }
        );
        
        toast.success('Design revised successfully!');
        await refreshCredits();
        
        const newDesign = {
          design_id: response.data.design_id,
          pages: response.data.design?.pages || [],
          elementor_json: response.data.design,
          version: response.data.version,
          created_at: new Date().toISOString()
        };
        setDesigns([newDesign, ...designs]);
        setCurrentDesign(newDesign);
        setRevisionPrompt('');
        setRevising(false);
      } else {
        // Advanced revision with polling
        const response = await axios.post(
          `${API}/ai/revise-design/advanced`,
          {
            design_id: currentDesign.design_id,
            revision_prompt: revisionPrompt
          },
          { withCredentials: true }
        );

        const jobId = response.data.job_id;
        setAdvancedJobId(jobId);
        setAdvancedProgress('Starting revision...');
        toast.info('Advanced revision started. Please wait...');
        
        // Poll for status
        const pollInterval = setInterval(async () => {
          try {
            const statusRes = await axios.get(
              `${API}/ai/revise-design/status/${jobId}`,
              { withCredentials: true }
            );
            
            setAdvancedProgress(statusRes.data.progress);
            
            if (statusRes.data.status === 'completed') {
              clearInterval(pollInterval);
              setAdvancedJobId(null);
              setRevising(false);
              
              toast.success('Design revised successfully!');
              await refreshCredits();
              
              const newDesign = {
                design_id: statusRes.data.design_id,
                pages: statusRes.data.design?.pages || [],
                elementor_json: statusRes.data.design,
                version: statusRes.data.version,
                created_at: new Date().toISOString()
              };
              setDesigns([newDesign, ...designs]);
              setCurrentDesign(newDesign);
              setRevisionPrompt('');
            } else if (statusRes.data.status === 'failed') {
              clearInterval(pollInterval);
              setAdvancedJobId(null);
              setRevising(false);
              toast.error(statusRes.data.error || 'Revision failed');
            }
          } catch (pollErr) {
            console.error('Poll error:', pollErr);
          }
        }, 2000);
      }
    } catch (error) {
      console.error('Revision error:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to revise design';
      
      if (error.response?.status === 422) {
        toast.error(errorMsg, { duration: 5000 });
      } else {
        toast.error(errorMsg);
      }
    } finally {
      if (revisionMode === 'quick') {
        setRevising(false);
      }
    }
  };

  const handleShare = async () => {
    setSharingLoading(true);
    try {
      const response = await axios.post(
        `${API}/projects/${projectId}/share`,
        {},
        { withCredentials: true }
      );
      
      const fullShareUrl = `${window.location.origin}/shared/${response.data.share_token}`;
      setShareUrl(fullShareUrl);
      setShowShareModal(true);
    } catch (error) {
      toast.error('Failed to generate share link');
      console.error(error);
    } finally {
      setSharingLoading(false);
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success('Link copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error('Failed to copy link');
    }
  };

  const refreshPreview = () => {
    setPreviewLoading(true);
    setPreviewError(null);
    setPreviewTimeout(false);
    // Force iframe refresh by updating token
    const generateNewToken = async () => {
      try {
        const res = await axios.post(
          `${API}/designs/${currentDesign.design_id}/preview-token`,
          {},
          { withCredentials: true }
        );
        setPreviewToken(res.data.token);
      } catch (err) {
        console.error('Failed to refresh preview:', err);
        setPreviewError('Failed to refresh preview. Please try again.');
        setPreviewLoading(false);
      }
    };
    generateNewToken();
  };

  // Handle iframe load error
  const handleIframeError = () => {
    setPreviewError('Failed to load WordPress preview. The staging site may be unavailable.');
    setPreviewLoading(false);
  };

  // Update images with Unsplash
  const handleUpdateImages = async () => {
    if (!currentDesign?.design_id) return;
    
    setUpdatingImages(true);
    try {
      const response = await axios.post(
        `${API}/designs/${currentDesign.design_id}/update-images`,
        {},
        { withCredentials: true }
      );
      
      toast.success(response.data.message || 'Images updated successfully!');
      setImageSource('unsplash');
      
      // Update the current design with new images
      setCurrentDesign({
        ...currentDesign,
        images: response.data.images
      });
      
      // Refresh the preview
      refreshPreview();
    } catch (err) {
      console.error('Failed to update images:', err);
      toast.error('Failed to update images. Please try again.');
    } finally {
      setUpdatingImages(false);
    }
  };

  // Get viewport width based on mode
  const getViewportWidth = () => {
    switch (responsiveViewMode) {
      case 'mobile': return '375px';
      case 'tablet': return '768px';
      default: return '100%';
    }
  };

  // Build preview URL with design_id, page, and token
  const getPreviewUrl = () => {
    if (!currentDesign?.design_id || !previewToken) return null;
    
    const currentPage = currentDesign.pages?.[activePage];
    const pageSlug = currentPage?.slug || 'home';
    
    // Always use the staging homepage as base, WordPress will render the right content
    // The preview plugin handles content injection based on design_id
    let pagePath = '';
    if (pageSlug && pageSlug !== 'home' && pageSlug !== 'homepage') {
      pagePath = `/${pageSlug}`;
    }
    
    return `${WP_STAGING_URL}${pagePath}/?design_id=${currentDesign.design_id}&token=${previewToken}`;
  };
  
  // Direct link to staging site (without design injection)
  const getStagingUrl = () => {
    return WP_STAGING_URL;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <Loader2 className="w-12 h-12 animate-spin text-indigo-600" />
      </div>
    );
  }

  if (!currentDesign) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-2 text-zinc-900">No design found</h2>
          <p className="text-zinc-600 mb-6">This project doesn&apos;t have a generated design yet.</p>
          <div className="flex gap-3 justify-center">
            <Button 
              onClick={() => navigate(`/themes/${projectId}`)}
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              Select Theme & Generate
            </Button>
            <Button variant="outline" onClick={() => navigate('/dashboard')}>Back to Dashboard</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50">
      {/* Header */}
      <header className="px-6 py-4 bg-white border-b border-zinc-200">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Dashboard
            </Button>
            <div className="h-6 w-px bg-zinc-200"></div>
            <WowCXLogo size="sm" />
            {project.display_id && (
              <span className="text-xs font-mono bg-zinc-100 text-zinc-600 px-2 py-0.5 rounded">
                {project.display_id}
              </span>
            )}
            <div>
              <h1 className="text-lg font-bold text-zinc-900" style={{fontFamily: 'Outfit'}}>{project.business_info.business_name}</h1>
              <p className="text-xs text-zinc-500">Version {currentDesign.version}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <CreditsDisplay compact showTopUp={false} />
            <Button
              data-testid="export-design-btn"
              variant="outline"
              onClick={() => setShowExportModal(true)}
            >
              <Download className="w-4 h-4 mr-2" />
              Export
            </Button>
            <Button
              data-testid="share-design-btn"
              variant="outline"
              onClick={handleShare}
              disabled={sharingLoading}
            >
              {sharingLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Share2 className="w-4 h-4 mr-2" />}
              Share
            </Button>
            {project.status !== 'frozen' && project.status !== 'pending_review' && (
              <Button
                data-testid="freeze-design-btn"
                onClick={() => navigate(`/place-order/${projectId}${currentDesign ? `?design=${currentDesign.design_id}` : ''}`)}
                className="bg-green-600 hover:bg-green-700"
              >
                <Lock className="w-4 h-4 mr-2" />
                Freeze Design
              </Button>
            )}
            {project.status === 'frozen' && (
              <div className="flex items-center gap-2 px-4 py-2 bg-green-100 rounded-lg">
                <Lock className="w-4 h-4 text-green-600" />
                <span className="text-sm font-medium text-green-700">Frozen</span>
              </div>
            )}
            <Button
              data-testid="logout-btn"
              variant="outline"
              size="sm"
              onClick={async () => {
                try {
                  await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
                  navigate('/');
                } catch (err) {
                  navigate('/');
                }
              }}
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <div className="p-6 md:p-8">
        <div className="max-w-[1600px] mx-auto">
          {/* Design Preview - WordPress Iframe - Full Width */}
          <div className="w-full">
            <Card className="bg-white overflow-hidden">
              <CardHeader className="flex flex-row items-center justify-between bg-white border-b">
                <CardTitle className="text-zinc-900">Design Preview</CardTitle>
                <PreviewToolbar
                  responsiveViewMode={responsiveViewMode}
                  setResponsiveViewMode={setResponsiveViewMode}
                  onRefresh={refreshPreview}
                  inspectMode={inspectMode}
                  onToggleInspectMode={toggleInspectMode}
                  iframeReady={iframeReady}
                  previewLoading={previewLoading}
                  imageSource={imageSource}
                  updatingImages={updatingImages}
                  onUpdateImages={handleUpdateImages}
                  viewMode={viewMode}
                  setViewMode={setViewMode}
                />
              </CardHeader>
              <CardContent className="bg-zinc-100 p-0">
                {/* Page Tabs */}
                <div className="bg-white border-b px-4">
                  <Tabs value={`page-${activePage}`} onValueChange={(val) => setActivePage(parseInt(val.replace('page-', '')))}>
                    <TabsList className="w-full justify-start bg-transparent h-auto py-2">
                      {(currentDesign.pages || []).map((page, index) => (
                        <TabsTrigger 
                          key={index} 
                          value={`page-${index}`} 
                          data-testid={`page-tab-${index}`}
                          className="data-[state=active]:bg-indigo-100 data-[state=active]:text-indigo-700 text-zinc-600 rounded-lg"
                        >
                          {page.page_name}
                        </TabsTrigger>
                      ))}
                      {project.status !== 'frozen' && project.status !== 'pending_review' && (
                        <button
                          onClick={() => setShowAddPageModal(true)}
                          className="ml-1 px-3 py-1.5 rounded-md text-sm font-medium bg-indigo-100 text-indigo-700 hover:bg-indigo-200 transition-colors flex items-center gap-1"
                          data-testid="add-page-btn"
                          title="Add new page"
                        >
                          <Plus className="w-4 h-4" />
                          Add Page
                        </button>
                      )}
                    </TabsList>
                  </Tabs>
                </div>

                {/* Theme Info Bar */}
                {selectedTheme && (
                  <div className="bg-white border-b px-4 py-3">
                    <div className="flex items-center justify-between flex-wrap gap-3">
                      <div className="flex items-center gap-3">
                        <span className="text-sm text-zinc-500">Theme:</span>
                        <button
                          onClick={() => setShowThemePreview(true)}
                          className="group flex items-center gap-2 px-3 py-1.5 bg-zinc-50 rounded-lg border border-zinc-200 hover:border-indigo-300 hover:bg-indigo-50 transition-all"
                          data-testid="theme-name-link"
                        >
                          <span className="font-semibold text-zinc-900 group-hover:text-indigo-600">{selectedTheme.name}</span>
                          <ExternalLink className="w-3.5 h-3.5 text-zinc-400 group-hover:text-indigo-500" />
                        </button>
                        <span className="text-zinc-300">|</span>
                        <a
                          href={getStagingUrl()}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="group flex items-center gap-2 px-3 py-1.5 bg-green-50 rounded-lg border border-green-200 hover:border-green-400 hover:bg-green-100 transition-all"
                          data-testid="staging-link"
                        >
                          <span className="text-sm font-medium text-green-700 group-hover:text-green-800">View Live Staging Site</span>
                          <ExternalLink className="w-3.5 h-3.5 text-green-500 group-hover:text-green-600" />
                        </a>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-5 h-5 rounded border shadow-sm" style={{backgroundColor: selectedTheme?.color_scheme?.primary}} title={`Primary: ${selectedTheme?.color_scheme?.primary}`}></div>
                        <div className="w-5 h-5 rounded border shadow-sm" style={{backgroundColor: selectedTheme?.color_scheme?.accent}} title={`Accent: ${selectedTheme?.color_scheme?.accent}`}></div>
                        <div className="w-5 h-5 rounded border shadow-sm" style={{backgroundColor: selectedTheme?.color_scheme?.secondary}} title={`Secondary: ${selectedTheme?.color_scheme?.secondary}`}></div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Preview Area */}
                <div className="p-4">
                  {viewMode === 'visual' ? (
                    <div 
                      className="bg-white rounded-lg shadow-lg mx-auto transition-all duration-300"
                      style={{ 
                        width: '100%',
                        maxWidth: '100%',
                        overflow: 'visible'
                      }}
                    >
                      {/* WordPress Iframe Preview */}
                      <PreviewIframe
                        previewUrl={getPreviewUrl()}
                        previewLoading={previewLoading}
                        setPreviewLoading={setPreviewLoading}
                        previewError={previewError}
                        previewTimeout={previewTimeout}
                        iframeRef={iframeRef}
                        onIframeError={handleIframeError}
                        onRefresh={refreshPreview}
                        onSwitchToTechnical={() => setViewMode('technical')}
                        inspectMode={inspectMode}
                        selectedElement={selectedElement}
                        clearElementSelection={clearElementSelection}
                        handleEditElement={handleEditElement}
                        toggleInspectMode={toggleInspectMode}
                        responsiveViewMode={responsiveViewMode}
                      />
                    </div>
                  ) : (
                    /* Technical View - JSON Structure */
                    <div className="bg-white rounded-lg p-6 space-y-6">
                      {(currentDesign.pages?.[activePage]?.sections || []).map((section, sectionIndex) => (
                        <div key={sectionIndex} className="border border-zinc-200 rounded-lg p-6">
                          <h3 className="text-lg font-bold mb-2" style={{fontFamily: 'Outfit'}}>{section.section_name}</h3>
                          <p className="text-sm text-zinc-600 mb-4">{section.layout}</p>
                          
                          {section.content && (
                            <div className="space-y-2">
                              {Object.entries(section.content).map(([key, value]) => (
                                <div key={key} className="bg-zinc-50 p-3 rounded">
                                  <div className="text-xs text-zinc-500 uppercase mb-1">{key.replace('_', ' ')}</div>
                                  <div className="text-sm">
                                    {typeof value === 'object' && value !== null 
                                      ? JSON.stringify(value, null, 2) 
                                      : value}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                          
                          {section.styling && (
                            <div className="mt-4 pt-4 border-t border-zinc-200">
                              <div className="text-xs text-zinc-500 mb-2">Styling</div>
                              <div className="flex gap-2 flex-wrap">
                                {Object.entries(section.styling).map(([key, value]) => (
                                  <div key={key} className="text-xs bg-indigo-50 px-2 py-1 rounded">
                                    <span className="font-medium">{key}:</span> {typeof value === 'object' ? JSON.stringify(value) : value}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Floating Chat Button */}
      <ChatToggleButton 
        onClick={() => setChatOpen(true)} 
        isOpen={chatOpen} 
      />

      {/* Floating Chat Panel */}
      <PreviewChatPanel
        chatOpen={chatOpen}
        setChatOpen={setChatOpen}
        sidePanelTab={sidePanelTab}
        setSidePanelTab={setSidePanelTab}
        revisionMode={revisionMode}
        setRevisionMode={setRevisionMode}
        revisionPrompt={revisionPrompt}
        setRevisionPrompt={setRevisionPrompt}
        revising={revising}
        advancedProgress={advancedProgress}
        credits={credits}
        selectedElement={selectedElement}
        clearElementSelection={clearElementSelection}
        project={project}
        projectId={projectId}
        currentDesign={currentDesign}
        handleReviseDesign={handleReviseDesign}
        onContentUpdated={refreshPreview}
      />

      {/* Share Modal */}
      <ShareModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        shareUrl={shareUrl}
        onCopy={copyToClipboard}
        copied={copied}
      />

      {/* Export Modal */}
      {showExportModal && currentDesign && (
        <ExportModal
          designId={currentDesign.design_id}
          pages={currentDesign.elementor_json?.pages || []}
          onClose={() => setShowExportModal(false)}
          elementorCompatible={currentDesign.elementor_compatible || false}
        />
      )}

      {/* Theme Preview Modal */}
      {selectedTheme && (
        <ThemePreviewModal
          theme={selectedTheme}
          isOpen={showThemePreview}
          onClose={() => setShowThemePreview(false)}
          onSelect={() => {}} 
          isSelected={true}
        />
      )}

      {/* Add Page Modal */}
      {showAddPageModal && currentDesign && (
        <AddPageModal
          projectId={projectId}
          designId={currentDesign.design_id}
          credits={credits}
          userEmail={user?.email}
          onClose={() => setShowAddPageModal(false)}
          onSuccess={(data) => {
            const newDesign = {
              design_id: data.design_id,
              pages: data.design?.pages || [],
              elementor_json: data.design,
              version: data.version,
              created_at: new Date().toISOString()
            };
            setDesigns([newDesign, ...designs]);
            setCurrentDesign(newDesign);
            refreshCredits();
            setShowAddPageModal(false);
          }}
        />
      )}
    </div>
  );
};

export default DesignPreviewPage;
